import { useEditorState } from '@tiptap/react'
import hljs from 'highlight.js/lib/core'
import { CODE_BLOCK_LANGUAGES } from '../../extensions/code-block-highlight'
import { useEditor } from '../../store/context'
import { MacroDropdown } from './MacroDropdown'

// A code block's raw `language` attribute may be any alias highlight.js accepts (`js`,
// `py`, `sh`, `yml`, ...), not just the ids used as our dropdown's option values.
// `getLanguage()` already resolves aliases to the language object it was registered
// under, so matching that object's identity against our own registered ids is enough to
// find the right option - no separate alias table needed.
function toSelectorLanguage(language: string | null | undefined): string {
  const resolved = language ? hljs.getLanguage(language) : undefined
  if (!resolved)
    return ''
  return CODE_BLOCK_LANGUAGES.find(l => l.value && hljs.getLanguage(l.value) === resolved)?.value ?? ''
}

export function Toolbar() {
  const { view, store } = useEditor()

  // Safe accessor: returns undefined until editor is ready
  const ed = store.ready ? store.editor() : undefined

  const currentHeading = useEditorState({
    editor: ed ?? null,
    selector: ({ editor }) => {
      if (!editor)
        return 'p'
      if (editor.isActive('heading', { level: 1 }))
        return '1'
      if (editor.isActive('heading', { level: 2 }))
        return '2'
      if (editor.isActive('heading', { level: 3 }))
        return '3'
      if (editor.isActive('heading', { level: 4 }))
        return '4'
      return 'p'
    },
  }) ?? 'p'

  const state = useEditorState({
    editor: ed ?? null,
    selector: ({ editor: e }) => {
      if (!e)
        return null
      const taskList = e.isActive('taskList')
      return {
        bold: e.isActive('bold'),
        italic: e.isActive('italic'),
        strike: e.isActive('strike'),
        code: e.isActive('code'),
        bulletList: e.isActive('bulletList'),
        orderedList: e.isActive('orderedList'),
        taskList,
        blockquote: e.isActive('blockquote'),
        codeBlock: e.isActive('codeBlock'),
        codeBlockLanguage: toSelectorLanguage(e.getAttributes('codeBlock').language as string | null),
        link: e.isActive('link'),
        canSink: e.can().sinkListItem(taskList ? 'taskItem' : 'listItem'),
        canLift: e.can().liftListItem(taskList ? 'taskItem' : 'listItem'),
      }
    },
  })

  function chain() {
    return store.editor().chain().focus()
  }

  function onHeadingChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value
    val === 'p'
      ? chain().setParagraph().run()
      : chain().toggleHeading({ level: Number.parseInt(val) as 1 | 2 | 3 | 4 }).run()
  }

  function onCodeBlockLanguageChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value
    chain().updateAttributes('codeBlock', { language: val || null }).run()
  }

  if (!store.ready)
    return null

  return (
    <div className="tiptap-toolbar">
      <div className="tiptap-toolbar-group">
        <select className="tiptap-select" value={currentHeading} onChange={onHeadingChange}>
          <option value="p">Paragraph</option>
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="4">Heading 4</option>
        </select>
      </div>

      <div className="tiptap-toolbar-group">
        <Btn title="Bold (Ctrl+B)" active={state?.bold} onCmd={() => chain().toggleBold().run()}>B</Btn>
        <Btn title="Italic (Ctrl+I)" active={state?.italic} onCmd={() => chain().toggleItalic().run()}>I</Btn>
        <Btn title="Strikethrough" active={state?.strike} onCmd={() => chain().toggleStrike().run()}>S</Btn>
        <Btn title="Inline Code" active={state?.code} onCmd={() => chain().toggleCode().run()}>{'</>'}</Btn>
        <Btn title="Clear Formatting" onCmd={() => chain().unsetAllMarks().clearNodes().run()}>✕</Btn>
      </div>

      <div className="tiptap-toolbar-group">
        <Btn title="Bullet List" active={state?.bulletList} onCmd={() => chain().toggleBulletList().run()}>•</Btn>
        <Btn title="Ordered List" active={state?.orderedList} onCmd={() => chain().toggleOrderedList().run()}>1.</Btn>
        <Btn title="Task List" active={state?.taskList} onCmd={() => chain().toggleTaskList().run()}>✔</Btn>
        <Btn title="Indent" disabled={!state?.canSink} onCmd={() => chain().sinkListItem(state?.taskList ? 'taskItem' : 'listItem').run()}>→</Btn>
        <Btn title="Outdent" disabled={!state?.canLift} onCmd={() => chain().liftListItem(state?.taskList ? 'taskItem' : 'listItem').run()}>←</Btn>
      </div>

      <div className="tiptap-toolbar-group">
        <Btn title="Blockquote" active={state?.blockquote} onCmd={() => chain().toggleBlockquote().run()}>"</Btn>
        <Btn title="Code Block" active={state?.codeBlock} onCmd={() => chain().toggleCodeBlock().run()}>{'{ }'}</Btn>
        <Btn title="Horizontal Rule" onCmd={() => chain().setHorizontalRule().run()}>─</Btn>
        <Btn title="Insert Table" onCmd={() => chain().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>▦</Btn>
      </div>

      {state?.codeBlock && (
        <div className="tiptap-toolbar-group">
          <select
            className="tiptap-select"
            title="Code block language"
            value={state?.codeBlockLanguage ?? ''}
            onChange={onCodeBlockLanguageChange}
          >
            {CODE_BLOCK_LANGUAGES.map(lang => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
          </select>
        </div>
      )}

      <div className="tiptap-toolbar-group">
        <Btn title="Insert Image" onCmd={() => store.openImageDialog()}>🖼</Btn>
        <Btn title="Insert Link (Ctrl+K)" active={state?.link} onCmd={() => store.openLinkDialog()}>🔗</Btn>
      </div>

      <div className="tiptap-toolbar-group">
        <MacroDropdown />
      </div>

      <div className="tiptap-toolbar-group">
        <Btn title="Editor only" active={view.viewMode === 'editor'} onCmd={() => view.setViewMode('editor')}>▣</Btn>
        <Btn title="Split view" active={view.viewMode === 'split'} onCmd={() => view.setViewMode('split')}>◫</Btn>
        <Btn title="Preview only" active={view.viewMode === 'preview'} onCmd={() => view.setViewMode('preview')}>👁</Btn>
        <Btn title="Toggle Markdown source" active={view.sourceMode} onCmd={() => view.toggleSourceMode()}>🪞</Btn>
        <Btn
          title="Fullscreen"
          active={view.fullscreen}
          onCmd={() => {
            const next = !view.fullscreen
            view.toggleFullscreen()
            document.body.style.overflow = next ? 'hidden' : ''
          }}
        >
          ⛶
        </Btn>
      </div>
    </div>
  )
}

interface BtnProps {
  title: string
  active?: boolean
  disabled?: boolean
  onCmd: () => void
  children: string
}

function Btn({ title, active, disabled, onCmd, children }: BtnProps) {
  return (
    <button
      type="button"
      className={`tiptap-btn${active ? ' is-active' : ''}`}
      disabled={disabled}
      title={title}
      onMouseDown={(e) => {
        e.preventDefault()
        onCmd()
      }}
    >
      {children}
    </button>
  )
}
